import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { apiUrl } from 'src/core/utils/api';

import { type ExpenseCategory } from '../expenseCategory';

export type BulkAssignResult = {
  failedCategories: ExpenseCategory[];
};

export const useBulkAssignCostCenters = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return async (
    expenseCategories: ExpenseCategory[],
    costCenterIds: string[],
  ): Promise<BulkAssignResult> => {
    const BATCH_SIZE = 10;
    const failedCategories: ExpenseCategory[] = [];

    const updateExpenseCategory = async (expenseCategory: ExpenseCategory) => {
      const response = await fetch(
        apiUrl(`/expense-categories/${expenseCategory.id}`, companyId),
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: expenseCategory.name,
            costCenterIds,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update expense category');
      }

      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return;
      }

      return response.json();
    };

    // Process expense categories in batches of 10
    for (let index = 0; index < expenseCategories.length; index += BATCH_SIZE) {
      const batch = expenseCategories.slice(index, index + BATCH_SIZE);
      const batchPromises = batch.map(async (category) => {
        try {
          await updateExpenseCategory(category);
          return { success: true as const };
        } catch {
          return { success: false as const, category };
        }
      });
      const results = await Promise.all(batchPromises);

      // Collect failed categories
      results.forEach((result) => {
        if (!result.success) {
          failedCategories.push(result.category);
        }
      });
    }

    await queryClient.invalidateQueries(['expenseCategories']);

    return { failedCategories };
  };
};
